import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn, ZoomOut } from 'lucide-react';
import { Document, Page } from '@/Components/shared/react-pdf-lazy';
import './pdf-viewer.css';

interface PdfViewerModalProps {
  pdfBlob: Blob | null;
  isOpen: boolean;
  onClose: () => void;
  warrantyName?: string;
}

export const PdfViewerModal: React.FC<PdfViewerModalProps> = ({
  pdfBlob,
  isOpen,
  onClose,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.25);

  // Le Blob est passé tel quel à <Document> : react-pdf sait le lire
  // directement, sans passer par une URL d'objet. On ne charge que modal ouvert.
  //
  // NE PAS revenir à `URL.createObjectURL` ici : l'URL était créée au render et
  // révoquée dans un cleanup d'effet, or StrictMode rejoue effet -> cleanup ->
  // effet à chaque montage. pdf.js recevait donc une URL déjà révoquée
  // (ERR_FILE_NOT_FOUND, « Unexpected server response (0) »).
  const fichierPdf = isOpen ? pdfBlob : null;

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(2.5, prev + 0.25));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(0.5, prev - 0.25));
  };

  // Bloquer le clic droit sur le PDF
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="tw-fixed tw-inset-0 tw-bg-black/80 tw-flex tw-items-center tw-justify-center tw-z-[9999] tw-p-4"
      onClick={onClose}
    >
      <div
        className="tw-bg-white tw-rounded-lg tw-shadow-2xl tw-max-h-[90vh] tw-flex tw-flex-col"
        onClick={(e) => e.stopPropagation()}
        onContextMenu={handleContextMenu}
      >
        {/* Controls */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-2 tw-py-1.5 tw-border-b tw-border-gray-200 tw-bg-gray-50">
          <span className="tw-text-xs tw-text-gray-600">
            {numPages} page{numPages > 1 ? 's' : ''}
          </span>

          <div className="tw-flex tw-items-center tw-gap-1">
            <button
              onClick={zoomOut}
              disabled={scale <= 0.5}
              className="tw-p-1 tw-text-gray-600 hover:tw-bg-gray-200 tw-rounded tw-transition-colors disabled:tw-opacity-50"
              title="Zoom arrière"
            >
              <ZoomOut className="tw-w-4 tw-h-4" />
            </button>
            <span className="tw-text-xs tw-text-gray-600 tw-min-w-[45px] tw-text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={zoomIn}
              disabled={scale >= 2.5}
              className="tw-p-1 tw-text-gray-600 hover:tw-bg-gray-200 tw-rounded tw-transition-colors disabled:tw-opacity-50"
              title="Zoom avant"
            >
              <ZoomIn className="tw-w-4 tw-h-4" />
            </button>
            <div className="tw-w-px tw-h-4 tw-bg-gray-300 tw-mx-1"></div>
            <button
              onClick={onClose}
              className="tw-p-1 tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-200 tw-rounded tw-transition-colors"
              title="Fermer"
            >
              <X className="tw-w-4 tw-h-4" />
            </button>
          </div>
        </div>

        {/* PDF Viewer */}
        <div
          className="tw-flex-1 tw-overflow-auto tw-p-4 tw-bg-gray-100"
          onContextMenu={handleContextMenu}
        >
          {fichierPdf ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
              <Document
                file={fichierPdf}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <div className="tw-text-center tw-py-12">
                    <div className="tw-inline-block tw-animate-spin tw-w-8 tw-h-8 tw-border-4 tw-border-blue-600 tw-border-t-transparent tw-rounded-full" />
                    <p className="tw-text-gray-600 tw-mt-4">Chargement du PDF...</p>
                  </div>
                }
                error={
                  <div className="tw-text-center tw-py-12 tw-text-red-600">
                    <p>Erreur lors du chargement du PDF</p>
                  </div>
                }
              >
                {Array.from(new Array(numPages), (_, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="tw-shadow-lg"
                  />
                ))}
              </Document>
            </div>
          ) : (
            <div className="tw-text-center tw-py-12 tw-text-gray-600">
              <p>Aucun PDF disponible</p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
